import * as React from 'react';
import deepEqual from 'fast-deep-equal';
import withFirestore from './withFirestore';

export interface CollectionState {
  data: any[];
  snapshot: firebase.firestore.QuerySnapshot;
  isLoading: boolean;
  error: firebase.firestore.FirestoreError;
}

type RenderFunc = (args: CollectionState) => React.ReactNode;

interface PropsQuery {
  sort?: string;
  limit?: number;
  filter?: [string, firebase.firestore.WhereFilterOp, any][];
}

interface Props extends PropsQuery {
  path: string;
  firestore: firebase.firestore.Firestore;
  children?: RenderFunc;
  render?: RenderFunc;
}

type State = CollectionState;

class FirestoreCollection extends React.Component<Props, State> {
  state: State = {
    isLoading: true,
    data: [],
    error: null,
    snapshot: null,
  };

  componentDidMount() {
    this.setupFirestoreListener();
  }

  componentWillReceiveProps(nextProps: Props) {
    const { path, sort, limit, filter } = this.props;
    if (
      nextProps.path !== path ||
      nextProps.sort !== sort ||
      nextProps.limit !== limit ||
      !deepEqual(nextProps.filter, filter)
    ) {
      this.handleUnsubscribe();

      this.setState({ isLoading: true }, () => this.setupFirestoreListener());
    }
  }

  componentWillUnmount() {
    this.handleUnsubscribe();
  }

  setupFirestoreListener = () => {
    const { firestore, path, ...queryProps } = this.props;
    const collectionRef = firestore.collection(path);
    const query = this.buildQuery(collectionRef, queryProps);

    this.unsubscribe = query.onSnapshot(this.handleOnSnapshotSuccess, this.handleOnSnapshotError);
  };

  handleOnSnapshotSuccess = (snapshot: firebase.firestore.QuerySnapshot) => {
    if (snapshot) {
      this.setState({
        isLoading: false,
        data: snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })),
        error: null,
        snapshot,
      });
    }
  };

  handleOnSnapshotError = (error: firebase.firestore.FirestoreError) => {
    this.setState({
      isLoading: false,
      data: [],
      error,
      snapshot: null,
    });
  };

  buildQuery = (collectionRef: firebase.firestore.Query, queryProps: PropsQuery) => {
    const { sort, limit, filter } = queryProps;
    let query = collectionRef;

    if (sort) {
      sort.split(',').forEach(sortItem => {
        const [field, order] = sortItem.split(':');
        query = query.orderBy(field, order as firebase.firestore.OrderByDirection);
      });
    }

    if (limit) {
      query = query.limit(limit);
    }

    if (filter) {
      filter.forEach(clause => {
        query = query.where(...clause);
      });
    }

    return query;
  };

  unsubscribe: () => void;

  handleUnsubscribe() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  render() {
    const { children, render } = this.props;

    if (render) return render(this.state);

    if (typeof children === 'function') return children(this.state);

    return null;
  }
}

export default withFirestore(FirestoreCollection);
